import { Component, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { forkJoin, Observable, of, Subscription, switchMap } from 'rxjs';
import { PortInDraftsResource } from '../port-in-orders/port-in-drafts.resource';
import { ModalBodyClass } from '../../../modals/modal-body';
import { ZiroFormConfig, ZiroFormsUpdate } from '../../../forms/ziro-forms-models';
import { ZiroFormStateService } from '../../../forms/ziro-form-state.service';
import { DetailedModalComponent } from '../../../modals/detailed-modal/detailed-modal.component';
import { ZiroSelectConfig } from '../../../forms/fields/select/ziro-select.component';
import { DocumentType, PortInOrderDraft } from '../../../shared/models/generated/ziroModels';
import { ZiroFileUploaderConfig, UploaderType, ZiroFile } from '../../../shared/file-uploader/file-uploader.component';
import { ModalViewProperties } from '../../../modals/modal-view-properties';

interface PortInDraftFileUploadEntity {
  documentType: string;
  file: ZiroFile<DocumentType>[];
}

@Component({
  selector: 'ziro-port-in-drafts-document-upload-modal',
  templateUrl: './port-in-drafts-upload-modal.component.html',
  providers: [PortInDraftsResource],
  standalone: false,
})
export class PortInDraftsUploadModalComponent extends ModalBodyClass implements OnInit, OnDestroy {
  protected _ziroFormStateService: ZiroFormStateService;
  private _modalComponent = inject<DetailedModalComponent<any>>(DetailedModalComponent);
  private _portInDraftsResource = inject(PortInDraftsResource);

  @ViewChild('errorTemplate') errorTemplate: TemplateRef<any>;

  formConfig: ZiroFormConfig;
  modalViewProperties: ModalViewProperties;

  private _draft: PortInOrderDraft;
  private _subscriptions = new Subscription();

  ngOnInit(): void {
    this._draft = this._modalComponent.modalViewProperties.draft;
    this.modalViewProperties = this._modalComponent.modalViewProperties;

    this._initFormConfig();
    this.entitySource.next({
      documentType: null,
    });

    const sub = this.ziroFormsUpdate$.subscribe((data: ZiroFormsUpdate<PortInDraftFileUploadEntity>) => {
      if (data.new.documentType && data.new.documentType !== data.old?.documentType) {
        switch (data.new.documentType) {
          case 'LOA':
          case 'INVOICE': {
            this._rebuildFileUploader(data.new.documentType, '.pdf');
            break;
          }
          case 'CSR': {
            this._rebuildFileUploader(data.new.documentType, '.pdf,.doc,.docx,.xls,.xlsx');
            break;
          }
          case 'OTHER':
          default: {
            this._rebuildFileUploader(data.new.documentType, '');
            break;
          }
        }
      }
    });
    this._subscriptions.add(sub);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subscriptions.unsubscribe();
  }

  submit(): Observable<boolean> {
    const requests: Observable<any>[] = [];
    const entity: PortInDraftFileUploadEntity = this.entity;
    entity.file.forEach((file) => {
      requests.push(this._portInDraftsResource.uploadDocument(this._draft.draftId, file.metadata, file.file));
    });

    return forkJoin(requests).pipe(
      switchMap(() => {
        return of(true);
      }),
    );
  }

  private _rebuildFileUploader(documentType: string, validFileTypes: string): void {
    this.fieldComponents.forEach((item) => {
      if (item.fieldId() === 'file') {
        item.applyComponentConfig(
          new ZiroFileUploaderConfig({
            acceptedFileExtensions: {
              acceptedExtensions: validFileTypes,
              allowOthers: validFileTypes === '',
            },
            maxKbSize: 3072,
            uploaderType: UploaderType.STAGED,
            maxFileNameLength: 50,
            metadataFn: () => documentType,
          }),
        );
      }
    });
  }

  private _initFormConfig(): void {
    this.formConfig = {
      fields: {
        documentType: {
          label: 'tkey;admin.order_numbers.port_in_drafts_edit.documents.modal.document_type.label',
          dataAutomation: 'port-in-draft-modal-document-type',
          componentConfig: new ZiroSelectConfig({
            options: [DocumentType.CSR, DocumentType.LOA, DocumentType.INVOICE, DocumentType.OTHER],
          }),
          required: true,
        },
        file: {
          required: true,
          dataAutomation: 'port-in-draft-modal-file',
          componentConfig: new ZiroFileUploaderConfig({
            acceptedFileExtensions: { acceptedExtensions: '', allowOthers: false },
            maxKbSize: 3072,
            uploaderType: UploaderType.STAGED,
            maxFileNameLength: 50,
          }),
          disabled: () => {
            return !this.formData.documentType;
          },
          disabledTooltip: 'tkey;admin.order_numbers.port_in_drafts_edit.documents.modal.document.tooltip',
        },
      },
    };
  }
}
